<template lang="pug">
    .article.full-height-error.not-valid(v-if="!enabledExtensions.includes('shop')")
        Error(:message="null" :code="403")
    .article.full-height-error.not-found(v-else-if="notFound")
        Error(:message="$t('Article Not Found')")
    .article.full-height-error(v-else-if="error")
        Error(:message="$t('Something went wrong!')" :code="501")
    section.article(v-else)
        //- a single tab needs no tab bar
        template(v-if="activeTranslation || !hasFurtherTabs")
            MainTab
        el-tabs(v-else tab-position="left" stretch v-model="tab")
            el-tab-pane(name="main" :label="$t('Article')")
                MainTab
            //- a single-article product's prices live on the product
            el-tab-pane(v-if="!isSingleArticleProduct" name="prices" :label="$t('Prices')")
                PricesTab
            //- Which dates this ticket variant admits to. Only for event products - a coupon or a
            //- physical article has no dates.
            el-tab-pane(v-if="isEventTicket" name="slots" :label="$t('Slots')")
                SlotsTab
            el-tab-pane(v-if="manageStock" name="stock" :label="$t('Stock')")
                StocksTab

</template>

<i18n>
{
    "de": {
        "Article dependencies could not be fetched.": "Artikelabhängigkeiten konnten nicht abgerufen werden.",
        "Article could not be fetched.": "Artikel konnte nicht gefunden werden.",
        "Article could not be saved.": "Artikel konnte nicht gespeichert werden.",
        "Article saved successfully!": "Artikel erfolgreich gespeichert!",
        "Article Not Found": "Artikel nicht gefunden",
        "Article": "Artikel",
        "Prices": "Preise",
        "Stock": "Bestand",
        "Slots": "Slots"
    }
}
</i18n>

<script>
import _ from 'lodash';

import pinia from '@/pinia';
import { useSetupStore, useShopArticleStore, useShopProductStore } from '@/stores';

import Tabs from '@/components/Tabs';
import Error from '@/components/Error';

import MainTab from './Tabs/Main';
import PricesTab from './Tabs/Prices';
import StocksTab from './Tabs/Stocks';
import SlotsTab from './Tabs/Slots';

export default {
    name: 'ArticleForm',
    props: {
        article: {
            type: Object,
            required: true
        },
        manageStock: {
            type: Boolean,
            required: true
        }
    },
    data: () => ({
        error: false,
        notFound: false,
        activeTab: 'main'
    }),
    components: {
        Error,
        Tabs,
        MainTab,
        PricesTab,
        StocksTab,
        SlotsTab
    },
    watch: {
        activeCms(cms) {
            const reload = _.get(cms, 'reloadComponents', true);
            if (reload) {
                this.setInitializedFlag(false);
                this.initialize();
            }
        }
    },
    computed: {
        setupStore() {
            return useSetupStore(pinia);
        },
        // Only an event product's articles have dates to narrow.
        isEventTicket() {
            return useShopProductStore(pinia).form.type === 'event';
        },
        // a single-article product owns all shared data, its prices included
        isSingleArticleProduct() {
            return useShopProductStore(pinia).isSingleArticleProduct;
        },
        // prices, slots or stock next to the article tab
        hasFurtherTabs() {
            return !this.isSingleArticleProduct || this.isEventTicket || this.manageStock;
        },
        shopArticleStore() {
            return useShopArticleStore(pinia);
        },
        activeTranslation() {
            if (this.form.translationOrigin) return this.form
        },
        tab: {
            get() {
                return this.activeTab;
            },
            set(value) {
                this.$emit('change-tab', value);
            }
        },
        activeShop() {
            return this.activeCms.shop;
        },
        form() {
            return this.shopArticleStore.form;
        },
        initialized() {
            return this.shopArticleStore.formInitialized;
        },
        activeCms() {
            return this.setupStore.activeCms;
        },
        enabledExtensions() {
            return this.setupStore.enabledExtensions;
        }
    },
    methods: {
        initialize() {
            if (!this.enabledExtensions.includes('shop')) {
                this.setInitializedFlag(true);
                return;
            }

            if (this.initialized !== true) {
                this.setInitializedFlag(true);

                const fetchDependencies = () => {
                    let actions = [
                        this.fetchCustomerGroupOptions(this.activeShop.id),
                        this.fetchPropTypeOptions(this.activeShop.id),
                        this.fetchPropertyOptions(this.activeShop.id),
                        this.fetchStockOptions(this.activeShop.id),
                        this.fetchTaxOptions(this.activeShop.id),
                        this.fetchMenuOptions(this.activeCms.id)
                    ];
                    return Promise.all(actions);
                };

                this.error = false;
                this.updateLoading(true);

                const handleDependencyError = err => {
                    const { graphQLErrors } = err

                    let code = 501;
                    let message = 'An error occurred!';

                    if (graphQLErrors) {
                        code = _.get(graphQLErrors, '0.code') || code;
                        message = _.get(graphQLErrors, '0.message') || message;
                    }

                    let notify = true;
                    switch (code) {
                        case 401:
                            notify = false;
                            this.error = { code, message: 'You are not authorized' };
                            break;
                        case 403:
                            notify = false;
                            this.error = { code, message: 'You are not allowed to be here' };
                            break;
                        // case 404:
                        //     break;
                        default:
                            this.error = true;
                            break;
                    }

                    if (notify) {
                        this.$notify.error({
                            title: this.$t(message),
                            message: this.$t('Article dependencies could not be fetched.'),
                            iconClass: 'fas el-icon-fa-xmark',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000
                        });
                    }

                    this.loading = false;

                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err);
                }

                if (!this.article.id || this.article.id.startsWith('new:')) {
                    // store.dispatch('ext_shop_article/reset');

                    this.insertArticle(this.article);

                    // fetchDependencies().then(() => store.commit('ext_shop_product/updateRules', rules))
                    fetchDependencies()
                        .catch(handleDependencyError)
                        .finally(() => {
                            this.loading = false;
                            this.updateLoading(false);
                        });
                } else if (!isNaN(this.article.id)) {
                    // fetch options for dropdowns
                    fetchDependencies()
                        .then(() => {
                            // this.resetRules();
                            this.updateLoading(false);
                        })
                        .catch(handleDependencyError)

                    this.fetchArticle({ id: this.article.id, translation: !!this.article.translationOrigin })
                        .then(() => {
                            this.notFound = false;
                        })
                        .catch(err => {
                            this.updateLoading(false);
                            this.notFound = true;

                            /* eslint no-console: ["error", { allow: ["error"] }] */
                            console.error(err);

                            this.$notify.error({
                                title: this.$t('An error occurred!'),
                                message: this.$t('Article could not be fetched.'),
                                iconClass: 'fas el-icon-fa-times',
                                position: 'bottom-left',
                                customClass: 'dark',
                                duration: 8000
                            });
                        });
                } else {
                    this.loading = true;
                    this.error = {  code: 404, message: 'Article not found' };
                }
            } else {
                if (this.validateOnEnter === true) {
                    setTimeout(() => {
                        this.$refs.form.validate(() => {
                            // do nothing
                            // callback function is required :(
                        });
                    }, 100);
                }
            }
        },
        insertArticle(article) {
            this.shopArticleStore.insertArticle(article);
        },
        setInitializedFlag(flag) {
            this.shopArticleStore.setFormInitialized(flag);
        },
        updateLoading(value) {
            this.shopArticleStore.updateFormLoading(value);
        },
        fetchArticle(payload) {
            return this.shopArticleStore.fetchArticle(payload);
        },
        fetchCustomerGroupOptions(shopId) {
            return this.shopArticleStore.fetchCustomerGroupOptions(shopId);
        },
        fetchPropTypeOptions(shopId) {
            return this.shopArticleStore.fetchPropTypeOptions(shopId);
        },
        fetchPropertyOptions(shopId) {
            return this.shopArticleStore.fetchPropertyOptions(shopId);
        },
        fetchStockOptions(shopId) {
            return this.shopArticleStore.fetchStockOptions(shopId);
        },
        fetchTaxOptions(shopId) {
            return this.shopArticleStore.fetchTaxOptions(shopId);
        },
        fetchMenuOptions(cmsId) {
            return this.shopArticleStore.fetchMenuOptions(cmsId);
        }
    },
    mounted() {
        this.initialize();
    },
    destroyed() {
        this.setInitializedFlag(false);
    }
};
</script>

<style lang="sass" scoped>
.article
    &.full-height-error
        ::v-deep
            h1
                margin-top: 0
    ::v-deep
        .el-tabs__nav.is-left
            min-width: 160px
section.article
    padding-top: 0
    padding-bottom: 0
    height: 100%
    ::v-deep .el-tabs__content
        max-height: calc(100vh - 134px)
        overflow-y: auto
        overflow-x: clip
</style>
